/**
 * Centralized authentication helpers for API calls.
 * 
 * The tokens are automatically captured and stored by react-oidc-context
 * after OAuth callback. They're stored in sessionStorage by default.
 * 
 * Security note: Browser extensions with storage permissions can technically
 * read sessionStorage. This is a known limitation of SPA-based auth.
 * Mitigations: short-lived tokens with automaticSilentRenew, sessionStorage
 * (cleared on tab close) rather than localStorage.
 */

import { AuthContextProps } from 'react-oidc-context';

export type ApiAuthHeaders = Record<string, string> & {
  'Content-Type': string;
  'X-Api-Key': string;
  'Authorization': string;
};

export interface AuthTokens {
  idToken: string;
  apiKey: string;
}

/**
 * Extracts the id_token and custom:api_key from the authenticated user.
 * These are automatically captured by react-oidc-context after authentication.
 * 
 * @param auth - The auth context from useAuth()
 * @returns The tokens if available
 * @throws Error if authentication details are incomplete
 */
export function getAuthTokens(auth: AuthContextProps): AuthTokens {
  const apiKey = auth.user?.profile?.['custom:api_key'] as string | undefined;
  const idToken = auth.user?.id_token;

  if (!auth.isAuthenticated) {
    throw new Error('User is not authenticated.');
  }

  if (!idToken) {
    throw new Error('ID token is missing. User may need to re-authenticate.');
  }

  if (!apiKey) {
    throw new Error('API key is missing from user profile. Contact administrator.');
  }

  return { idToken, apiKey };
}

/**
 * Gets the authentication headers required for API calls.
 * 
 * @param auth - The auth context from useAuth()
 * @returns Headers object with Authorization (Bearer token) and X-Api-Key
 * @throws Error if authentication details are incomplete
 */
export function getApiAuthHeaders(auth: AuthContextProps): ApiAuthHeaders {
  const { idToken, apiKey } = getAuthTokens(auth);

  return {
    'Content-Type': 'application/json',
    'X-Api-Key': apiKey,
    'Authorization': idToken,  // Note: Some APIs expect "Bearer <token>", adjust if needed
  };
}

/**
 * Checks if the user has valid authentication tokens available.
 * Useful for conditional rendering or pre-flight checks.
 * 
 * @param auth - The auth context from useAuth()
 * @returns true if tokens are available, false otherwise
 */
export function hasValidAuthTokens(auth: AuthContextProps): boolean {
  try {
    getAuthTokens(auth);
    return true;
  } catch {
    return false;
  }
}
